import { Button, Icon, TextInput, theme } from '@ketch-com/deck'
import { Box, styled, Tooltip, Typography } from '@mui/material'
import { AuditLogActionVariant } from 'pages/insights/auditLog/components/AuditLogPopup'
import { useAuditLogContext } from 'pages/insights/auditLog/context/AuditLogContext'
import { useDebounce } from 'react-use'
import { useState } from 'react'
import { HistoricalPermitPopupVariant } from 'pages/insights/auditLog/components/historicalPermitPopup/constants'

export enum AuditLogHeaderActionsVariant {
  Consent = 'consent',
  Subscriptions = 'subscriptions',
}

export type AuditLogHeaderActionsProps = {
  variant: AuditLogHeaderActionsVariant
}

const HeaderActionsBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 0px 24px 0px',
})

const HeaderActionsLeftBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const HeaderActionsRightBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
})

export const AuditLogHeaderActions: React.FC<AuditLogHeaderActionsProps> = ({ variant }) => {
  const {
    consentData,
    triggerAuditLogPopup,
    topicSearch,
    setTopicSearch,
    setShowViewExperienceModal,
    setAuditLogHistoricalPermitPopupVariant,
    setAuditLogExperiencePreviewPermit,
  } = useAuditLogContext()

  const disableViewExperienceButton = !consentData?.[0]?.context?.configurationId

  const [localTopicSearch, setLocalTopicSearch] = useState(topicSearch)
  useDebounce(
    () => {
      setTopicSearch(localTopicSearch)
    },
    500,
    [localTopicSearch],
  )
  const isConsentTab = variant === AuditLogHeaderActionsVariant.Consent

  return (
    <>
      <HeaderActionsBox>
        <HeaderActionsLeftBox>
          <Typography color={theme.palette.Text.Secondary}>Status update:</Typography>
          <Button
            color="tertiary"
            startIcon={<Icon name="OCross" />}
            onClick={() =>
              triggerAuditLogPopup({
                variant: isConsentTab
                  ? AuditLogActionVariant.RejectAllConsent
                  : AuditLogActionVariant.UnsubscribeAllSubscriptions,
              })
            }
          >
            {isConsentTab ? 'Opt Out All' : 'Unsubscribe from All'}
          </Button>
          <Button
            color="tertiary"
            startIcon={<Icon name="OCheck" />}
            onClick={() =>
              triggerAuditLogPopup({
                variant: isConsentTab
                  ? AuditLogActionVariant.AcceptAllConsent
                  : AuditLogActionVariant.SubscribeAllSubscriptions,
              })
            }
          >
            {isConsentTab ? 'Opt In All' : 'Subscribe to All'}
          </Button>
          <Tooltip
            title={
              isConsentTab
                ? 'This will set each purpose to its default state based on its legal basis in the jurisdiction which the consent record originated.'
                : 'This will set each subscription to its default state based on its legal basis in the jurisdiction which the subscription record originated.'
            }
            placement="bottom"
          >
            <span>
              <Button
                color="tertiary"
                onClick={() =>
                  triggerAuditLogPopup({
                    variant: isConsentTab
                      ? AuditLogActionVariant.SetAllDefaultConsent
                      : AuditLogActionVariant.SetAllDefaultSubscriptions,
                  })
                }
              >
                Reset to Defaults
              </Button>
            </span>
          </Tooltip>
        </HeaderActionsLeftBox>
        <HeaderActionsRightBox>
          {isConsentTab ? (
            <>
              <Button
                color="secondary"
                onClick={() => setAuditLogHistoricalPermitPopupVariant(HistoricalPermitPopupVariant.Consent)}
              >
                View History
              </Button>
              <Tooltip
                title={
                  disableViewExperienceButton
                    ? 'You are unable to view the experience associated with this consent record. This is either because the consent was set non-interactively, or it was set before consent records were associated with experiences.'
                    : ''
                }
                placement="bottom"
              >
                <span>
                  <Button
                    color="secondary"
                    onClick={() => {
                      setAuditLogExperiencePreviewPermit(consentData?.[0] || null)
                      setShowViewExperienceModal(true)
                    }}
                    disabled={disableViewExperienceButton}
                  >
                    View Experience
                  </Button>
                </span>
              </Tooltip>
            </>
          ) : (
            <TextInput
              size="small"
              value={localTopicSearch}
              placeholder="Search for a topic"
              onChange={e => setLocalTopicSearch(e.target.value)}
              startIcon={<Icon name={'OMag'} iconColor={theme.palette.Black.o48} />}
            />
          )}
        </HeaderActionsRightBox>
      </HeaderActionsBox>
    </>
  )
}
